import { useEffect, useMemo, useState } from "react";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getEvents } from "@/store/slices/eventSlice";
import type { EventType } from "@/services/api/events";
import Pagination from "@/components/Table/Pagination";
import { formatINR } from "@/lib/currency";
import { toOffsetDateTime } from "@/lib/dateTime";
import GatheringsToolbar from "./components/GatheringsToolbar/GatheringsToolbar";
import GatheringCard from "./components/GatheringCard/GatheringCard";
// import PastGatherings from "./components/PastGatherings/PastGatherings";
import { EVENT_TYPE_LABELS, formatEventDate } from "./lib/eventFormat";
import styles from "./Gatherings.module.scss";


const ROWS_PER_PAGE = 9;

const Gatherings = () => {
  const dispatch = useAppDispatch();
  const { events, totalEvents, monthlyStats, isFetching } = useAppSelector((state) => state.event);

  const [activeType, setActiveType] = useState("all");
  const [pageSelected, setPageSelected] = useState(1);

  const fromDate = useMemo(() => toOffsetDateTime(new Date()), []);

  useEffect(() => {
    setPageSelected(1);
  }, [activeType]);

  useEffect(() => {
    dispatch(
      getEvents({
        pageNo: pageSelected,
        perPage: ROWS_PER_PAGE,
        fromDate,
        eventType: activeType === "all" ? undefined : (activeType as EventType),
      }),
    );
  }, [dispatch, pageSelected, fromDate, activeType]);

  const today = useMemo(() => new Date(), []);
  const upcomingEvents = events;

  const nextUpcoming = upcomingEvents
    .slice()
    .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())[0];
  const daysUntilNext = nextUpcoming
    ? Math.max(0, Math.round((new Date(nextUpcoming.eventDate).getTime() - today.getTime()) / 86_400_000))
    : null;

  // All three are real: "Upcoming Gatherings" is derived from the GET
  // /events list; the other two come from that same response's monthly
  // aggregates (`monthlyStats` in eventSlice.ts).
  const {
    totalSeatsThisMonth,
    seatsFilledThisMonth,
    ticketRevenueThisMonth,
    hikePercentage,
    lastMonthName,
  } = monthlyStats;
  const capacityPercent = totalSeatsThisMonth > 0 ? Math.round((seatsFilledThisMonth / totalSeatsThisMonth) * 100) : 0;

  const gatheringStats: StatCardData[] = [
    {
      label: "Upcoming Gatherings",
      value: totalEvents,
      caption: daysUntilNext !== null ? `next in ${daysUntilNext} day${daysUntilNext === 1 ? "" : "s"}` : "none scheduled",
    },
    {
      label: "Seats Filled This Month",
      value: `${seatsFilledThisMonth} / ${totalSeatsThisMonth}`,
      caption: `${capacityPercent}% capacity`,
    },
    {
      label: "Ticket Revenue (MTD)",
      value: formatINR(ticketRevenueThisMonth, "₹0"),
      delta: `${hikePercentage >= 0 ? "+" : ""}${hikePercentage}%`,
      deltaTone: hikePercentage < 0 ? "negative" : "positive",
      caption: lastMonthName ? `vs ${lastMonthName}` : "",
    },
  ];

  const tabs = useMemo(
    () => [
      { label: "All", value: "all" },
      ...(Object.keys(EVENT_TYPE_LABELS) as (keyof typeof EVENT_TYPE_LABELS)[]).map((eventType) => ({
        label: EVENT_TYPE_LABELS[eventType],
        value: eventType,
      })),
    ],
    [],
  );

  usePageSubtitle(
    isFetching
      ? "Loading gatherings…"
      : `${totalEvents} scheduled${nextUpcoming ? ` · next ${formatEventDate(nextUpcoming.eventDate)}` : ""}`,
  );

  return (
    <div className={styles.page}>
      <StatCardsRow stats={gatheringStats} />

      <GatheringsToolbar tabs={tabs} activeTab={activeType} onTabChange={setActiveType} />

      <div className={styles.grid}>
        {upcomingEvents.map((event) => (
          <GatheringCard key={event.id} event={event} />
        ))}
      </div>

      <div className={styles.paginationWrapper}>
        <Pagination
          totalCount={totalEvents}
          pageSelected={pageSelected}
          rowsPerPageValue={ROWS_PER_PAGE}
          setPageSelected={setPageSelected}
        />
      </div>

      {/* <PastGatherings events={pastEvents} /> */}
    </div>
  );
};

export default Gatherings;
